import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";
import { C } from "../lib/constants";
import { Btn } from "./ui/Btn";
import { Card } from "./ui/Card";
import { Chip } from "./ui/Chip";
import { TopNav } from "./ui/TopNav";
import { getFlowInfo, EstadoChip, FlowTimeline, MediationBox, EmptyBox, MediacionModal } from "./WorkFlow";

export const TrabajosCliente = ({setScreen, user, profile}) => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [mediacionTarget, setMediacionTarget] = useState(null);
  const [mediacionReason, setMediacionReason] = useState("");
  const userId = user?.id || null;

  const cargar = useCallback(async () => {
    setErrorMsg("");

    if (!userId) {
      setSolicitudes([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // FIX SESIÓN CRUZADA: SOLO filtrar por cliente_user_id.
    // Eliminado el filtro por cliente_telefono — podía retornar solicitudes
    // de otro cliente con el mismo número registrado.
    const { data, error } = await supabase
      .from("solicitudes")
      .select("*")
      .eq("cliente_user_id", userId)   // ESTRICTO: solo user_id
      .order("created_at", { ascending:false });

    if (error) {
      setErrorMsg(error.message);
      setSolicitudes([]);
    } else {
      setSolicitudes(data || []);
    }

    setLoading(false);
  }, [userId]);

  useEffect(() => {
    cargar();
    const chName = `solicitudes-cliente-${userId||"anon"}`;
    const channel = supabase
      .channel(chName)
      .on("postgres_changes", { event:"*", schema:"public", table:"solicitudes" }, cargar)
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [cargar, userId]);

  const actualizar = async (id, estado, extra={}) => {
    const payload = { estado, updated_at:new Date().toISOString(), ...extra };
    if (estado === "aceptada") {
      payload.cliente_autorizo = true;
      payload.authorized_at = new Date().toISOString();
    }
    if (estado === "completada") {
      payload.cliente_concluyo = true;
      payload.finished_at = new Date().toISOString();
    }
    if (estado === "mediacion_solicitada") {
      payload.mediation_requested_by = "cliente";
      payload.mediation_fee = 500;
      payload.mediation_requested_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from("solicitudes")
      .update(payload)
      .eq("id", id)
      .eq("cliente_user_id", userId);

    if (error) { window.ficioToast?.(error.message, "error"); return; }
    cargar();
  };

  const solicitarMediacion = async () => {
    if (!mediacionTarget) return;
    // FIX E6: quitar return duplicado que impedía ejecutar la mediación
    if (!mediacionReason.trim()) { window.ficioToast?.("Escribe el motivo de la mediación", "warn"); return; }

    await actualizar(mediacionTarget.id, "mediacion_solicitada", {
      mediation_reason: mediacionReason.trim(),
    });

    setMediacionTarget(null);
    setMediacionReason("");
  };

  if (!user) {
    return (
      <div style={{minHeight:"100vh",background:C.bg,paddingBottom:100}}>
        <TopNav setScreen={setScreen} back="landing" backLabel="Inicio" title="Mis trabajos"/>
        <div style={{maxWidth:640,margin:"0 auto",padding:"20px 20px 0"}}>
          <EmptyBox icon="🔐" title="Inicia sesión" desc="Para ver tus trabajos debes entrar con tu cuenta de cliente."/>
          <div style={{height:12}}/>
          <Btn full color={C.blue} onClick={()=>setScreen("cliente-login")}>Entrar a mi cuenta</Btn>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:C.bg,paddingBottom:100}}>
      <TopNav setScreen={setScreen} back="cuenta" backLabel="Cuenta" title="Mis trabajos"/>

      <div style={{maxWidth:640,margin:"0 auto",padding:"20px 20px 0"}}>
        <div style={{background:`linear-gradient(135deg,${C.blue},#123E73)`,borderRadius:22,padding:22,color:"#fff",marginBottom:16,boxShadow:"0 6px 22px rgba(37,88,168,.25)"}}>
          <div style={{fontSize:32,marginBottom:8}}>🔐</div>
          <div style={{fontFamily:"Georgia, serif",fontSize:25,fontWeight:900,marginBottom:4}}>Mis trabajos privados</div>
          <div style={{fontSize:13,opacity:.85,lineHeight:1.5}}>
            Esta sección solo se abre desde tu cuenta iniciada. Ya no se puede buscar por WhatsApp.
          </div>
        </div>

        <Card>
          <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:.8,textTransform:"uppercase",marginBottom:8}}>Cuenta activa</div>
          <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"center"}}>
            <div>
              <div style={{fontFamily:"Georgia, serif",fontSize:17,fontWeight:900,color:C.text}}>
                {profile?.nombre || user.email}
              </div>
              <div style={{color:C.muted,fontSize:12}}>Trabajos ligados a tu sesión</div>
            </div>
            <Chip text="Privado" color={C.green} small/>
          </div>
        </Card>

        {errorMsg && <div style={{background:C.redL,border:`1.5px solid ${C.red}35`,borderRadius:14,padding:"12px 14px",color:C.red,marginBottom:14,fontSize:12}}>{errorMsg}</div>}

        {loading ? (
          <div style={{textAlign:"center",padding:"42px 0",color:C.muted}}>Cargando trabajos...</div>
        ) : solicitudes.length === 0 ? (
          <EmptyBox icon="📭" title="Sin trabajos" desc="Cuando solicites una cotización desde tu cuenta aparecerá aquí."/>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {solicitudes.map(s => {
              const status = s.estado || s.status || "pendiente";
              const info = getFlowInfo(status);
              const puedeAutorizar = status === "pendiente";
              const puedeConcluir = status === "en_progreso";

              return (
                <div key={s.id} style={{background:C.surface,border:`1.5px solid ${info.color}30`,borderRadius:18,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:10}}>
                    <div>
                      <div style={{fontFamily:"Georgia, serif",fontSize:17,fontWeight:900,color:C.text}}>{s.trabajador_nombre || "Trabajador"}</div>
                      <div style={{color:C.muted,fontSize:11}}>📍 {s.zona || "Sin zona"} · {s.oficio || "Servicio"}</div>
                    </div>
                    <EstadoChip status={status}/>
                  </div>

                  <FlowTimeline status={status}/>
                  <MediationBox solicitud={s}/>

                  <div style={{background:C.bg,borderRadius:14,padding:13,marginBottom:12}}>
                    <div style={{color:C.sub,fontSize:12,lineHeight:1.5}}>{s.descripcion}</div>
                  </div>

                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {puedeAutorizar ? (
                      <Btn small full color={C.blue} onClick={()=>actualizar(s.id, "aceptada")}>Autorizar trabajo</Btn>
                    ) : puedeConcluir ? (
                      <Btn small full color={C.green} onClick={()=>actualizar(s.id, "completada", {concluded_by:"cliente"})}>Concluir trabajo</Btn>
                    ) : (
                      <Btn small full outline color={info.color} onClick={()=>window.ficioToast?.(`Estado: ${info.label}`, "info")}>{info.label}</Btn>
                    )}
                    <Btn small full outline color={C.red} onClick={()=>setMediacionTarget(s)}>⚖️ Mediación $500</Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {mediacionTarget && (
        <MediacionModal
          solicitud={mediacionTarget}
          reason={mediacionReason}
          setReason={setMediacionReason}
          onClose={()=>{setMediacionTarget(null);setMediacionReason("");}}
          onConfirm={solicitarMediacion}
        />
      )}
    </div>
  );
};
